import { useCallback, useMemo, useRef, useState } from "react";
import { useGUIEditorContext } from "../../../../../../hook/context/gui";
import { useDashboardEditorContext } from "../../../../../../hook/context/editor";
import type { RuleLabelT } from "../../../../../../schema/block/plot";

const formatRuleValue = (value: string) => {
  const v = value.replace(",", ".");
  if (!Number.isNaN(parseFloat(v))) return parseFloat(v);
  if (value === "") return null;
  return value;
};

export const useLayerClearRuleCallback = (position: number, axis: "x" | "y") => {
  const editor = useDashboardEditorContext();
  const { block } = useGUIEditorContext();

  const [withRuleOptions, setWithRuleOptions] = useState(() =>
    block?.config.layers[position]?.rule?.[axis]?.some(({ value }) => value !== null),
  );
  const previousRuleValues = useRef<
    { value: number | string | null; label: "min" | "max" | null | undefined }[] | undefined
  >([]);

  const editorLayerClearRuleCallback = useCallback(() => {
    if (!block) return;

    // previousRuleValues.current = block.config.layers.map(layer => layer.rule);

    void editor?.updateBlock({
      ...block,
      config: {
        ...block.config,
        layers: block.config.layers.map((layer, i) =>
          i === position
            ? {
                ...layer,
                rule: {
                  ...layer.rule,
                  [axis]: [{ value: null, label: null }],
                },
              }
            : layer,
        ),
      },
    });
  }, [axis, block, editor, position]);

  const editorLayerRevertRulesCallback = useCallback(() => {
    if (!block) return;
    void editor?.updateBlock({
      ...block,
      config: {
        ...block.config,
        layers: block.config.layers.map((layer, i) =>
          i === position
            ? {
                ...layer,
                rule: {
                  ...layer.rule,
                  [axis]: previousRuleValues.current,
                },
              }
            : layer,
        ),
      },
    });
  }, [axis, block, editor, position]);

  const toggleRuleOptions = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setWithRuleOptions(e.target.checked);
      if (!e.target.checked && block) {
        if (block?.config.layers[position]?.rule?.[axis]) {
          previousRuleValues.current = block?.config.layers[position]?.rule?.[axis] ?? undefined;
        }
        editorLayerClearRuleCallback();
      } else {
        editorLayerRevertRulesCallback();
      }
    },
    [axis, block, editorLayerClearRuleCallback, editorLayerRevertRulesCallback, position],
  );

  return useMemo(
    () => ({
      withRuleOptions,
      toggleRuleOptions,
    }),
    [toggleRuleOptions, withRuleOptions],
  );
};

export const useLayerRule = (position: number, axis: "x" | "y", index: number) => {
  const editor = useDashboardEditorContext();
  const { block } = useGUIEditorContext();
  const [value, setValue] = useState(block?.config.layers[position]?.rule?.[axis]?.[index]?.value);

  const editorUpdateLayerRule = useCallback(
    (value: string, label?: RuleLabelT) => {
      if (!block) return;

      void editor?.updateBlock({
        ...block,
        config: {
          ...block.config,
          layers: block.config.layers.map((layer, i) =>
            i === position
              ? {
                  ...layer,
                  rule: {
                    ...layer.rule,
                    [axis]: [{ value: null }, { value: null }].map((rule, j) => {
                      if (j === index) {
                        return { value: formatRuleValue(value), label: label ?? null };
                      }
                      return layer.rule?.[axis]?.[j] ?? rule;
                    }),
                  },
                }
              : layer,
          ),
        },
      });
    },
    [axis, block, editor, index, position],
  );

  const updateLayerRule = useCallback(
    (value: string, label?: RuleLabelT) => {
      setValue(value);
      editorUpdateLayerRule(value, label);
    },
    [editorUpdateLayerRule],
  );

  return { value, updateLayerRule };
};
